/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnDestroy} from '@angular/core';
import template from './line-item-creative-list.component.html';

import {asUrlGroupList, asUrlList} from '../creative/creative-url-utils';
import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {bufferTemplateSignalValue, createBufferedSignalValue} from '../common/util/template-state';

/** Embedded creative table on line item / creative set edit — not a standalone list page. */
@Component({
  standalone: false,
  selector: 'adn-line-item-creative-list',
  template
})
export class LineItemCreativeListComponent implements OnDestroy {
  @Input() resource: any;
  @Input() readOnly: boolean;

  showCreativeSizes = false;

  private readonly listResolvedState = createBufferedSignalValue(false);
  private destroyed = false;

  get listItems(): any[] { return this.resource?.listState?.items() || []; }
  get listTotalCount(): number { return this.resource?.listState?.totalCount() || 0; }
  get listPageSize(): number { return this.resource?.listState?.pageSize || 25; }
  get listResolved(): boolean {
    const listState = this.resource?.listState;
    return bufferTemplateSignalValue(
      this.listResolvedState,
      !!listState && !listState.loading(),
      () => !this.destroyed
    );
  }
  get isNewResource(): boolean { return !!this.resource?.isNew; }
  get hasItems(): boolean { return this.listItems.length > 0; }
  get showPagination(): boolean { return this.listTotalCount > this.listPageSize || this.listTotalCount > 5; }
  get availableFixed(): any[] { return this.resource?.availableFixed || []; }
  get availableRanges(): any[] { return this.resource?.availableRanges || []; }
  get hasDeliverableSizes(): boolean {
    return !!this.resource?.checksDone && !!(this.availableFixed.length || this.availableRanges.length);
  }
  get unmatchedCreatives(): Record<string, any> { return this.resource?.unmatchedCreatives || {}; }

  readonly sortHeaderParams = [
    {param: 'name', label: 'creative'},
    {label: 'info', param: '', sortable: false},
    {param: '', label: 'targeting', sortable: false}
  ];

  readonly itemStateName = 'app.adv.creatives.creative';
  readonly newItemStateName = 'app.adv.creatives.new';
  readonly bulkUploadStateName = 'app.adv.creatives.bulkupload';
  readonly layoutItemStateName = 'app.adm.layouts.layout';
  readonly externalDemandSourceItemStateName = 'app.adm.external-demand-sources.external-demand-source';

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  hideBulkSelect(): boolean {
    return !!this.resource?.readOnly || !!this.resource?.isAxPublisher;
  }

  creativeLinkParams(creative: any): any {
    return {
      fromLineItem: !this.resource?.isCreativeSet,
      fromCreativeSet: !!this.resource?.isCreativeSet,
      id: creative.id
    };
  }

  newCreativeParams(): any {
    const model = this.resource?.model || {};
    return {
      fromCreativeSet: !!this.resource?.isCreativeSet,
      fromLineItem: !this.resource?.isCreativeSet,
      modelName: model.name,
      modelId: model.id,
      advertiserName: model.advertiser?.name,
      advertiserId: model.advertiser?.id
    };
  }

  bulkUploadParams(): any {
    const model = this.resource?.model || {};
    const advertiserParams = model.advertiser ? advertiserNavigationParams(model.advertiser) : {};
    const parentParams = this.resource?.isCreativeSet
      ? {creativeSetId: model.id, creativeSetName: model.name}
      : {lineItemId: model.id, lineItemName: model.name};
    return {...advertiserParams, ...parentParams};
  }

  isUnmatchedCreative(creative: any): boolean {
    return !!this.unmatchedCreatives?.[creative.id];
  }

  undeliverableMessage(creative: any): string {
    return `The targeted inventory does not support the delivery of ${creative.width}×${creative.height} creatives`;
  }

  layoutLinkDisabled(): boolean {
    return this.resource?.perms?.MANAGE_LAYOUTS !== true;
  }

  externalDemandLinkDisabled(): boolean {
    return this.resource?.perms?.MANAGE_SYSTEM !== true;
  }

  constraintUrls(creative: any): string[] {
    return asUrlList(creative?.constraintsToUrls);
  }

  assetHtmlUrlGroups(creative: any): string[][] {
    return asUrlGroupList(creative?.assetHtmlUrls);
  }
}
